import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShoppingBag, ArrowRight, Shield, Truck } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useTrackProductView } from "@/lib/affiliateTracking";
import { addToCart } from "@/lib/cart";
import SafeImage from "@/components/SafeImage";

function priceLabel(p) {
  const cents = p?.price_cents ?? Math.round((p?.price || 0) * 100);
  if (!cents) return "Price not available";
  return `$${(cents / 100).toFixed(2)} USD`;
}

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [added, setAdded] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState(null);

  useEffect(() => {
    setAdded(false);
    setSelectedVariantId(null);
    (async () => {
      try {
        const { data } = await api.get(`/shop/products/${slug}`);
        setP(data);
        const firstAvail = (data?.variants || []).find(v => v.available);
        if (firstAvail) setSelectedVariantId(firstAvail.id);
      }
      catch (e) { setErr(apiErrorMessage(e)); }
    })();
  }, [slug]);

  useTrackProductView(slug);

  const variants = useMemo(() => Array.isArray(p?.variants) ? p.variants : [], [p]);
  const hasVariants = variants.length > 0;
  const selectedVariant = useMemo(
    () => variants.find(v => v.id === selectedVariantId) || null,
    [variants, selectedVariantId],
  );

  const displayCents = useMemo(() => {
    if (selectedVariant?.price_cents) return selectedVariant.price_cents;
    return p?.price_cents ?? Math.round((p?.price || 0) * 100);
  }, [p, selectedVariant]);

  const hasPrice = (displayCents || 0) > 0;
  const variantBlocked = hasVariants && !selectedVariantId;

  if (err) return <div className="max-w-7xl mx-auto px-6 py-20 text-rose-300" data-testid="product-error">{err}</div>;
  if (!p) return <div className="max-w-7xl mx-auto px-6 py-20 font-bebas tracking-[0.3em] text-lifted-dim">LOADING…</div>;

  const onAdd = () => {
    if (!hasPrice || variantBlocked) return;
    const cartItem = selectedVariant
      ? { ...p, price_cents: selectedVariant.price_cents,
          variant_id: selectedVariant.id, variant_title: selectedVariant.title,
          square_variation_id: selectedVariant.square_variation_id,
          shopify_variant_id: selectedVariant.shopify_variant_id || p.shopify_variant_id,
          sku: selectedVariant.sku || p.sku }
      : p;
    addToCart(cartItem);
    setAdded(true);
  };

  const onBuyNow = () => {
    if (!hasPrice || variantBlocked) return;
    const cartItem = selectedVariant
      ? { ...p, price_cents: selectedVariant.price_cents,
          variant_id: selectedVariant.id, variant_title: selectedVariant.title,
          square_variation_id: selectedVariant.square_variation_id,
          shopify_variant_id: selectedVariant.shopify_variant_id || p.shopify_variant_id,
          sku: selectedVariant.sku || p.sku }
      : p;
    addToCart(cartItem);
    nav("/cart");
  };

  const media = (p.media || []).filter(Boolean);
  const galleryItems = media.length ? media : (p.image ? [p.image] : []);

  return (
    <div data-testid={`product-${slug}`} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div className="space-y-3" data-testid="product-gallery">
          {galleryItems.length === 0 && (
            <div className="aspect-[3/4] rounded-xl bg-lifted-surface border border-white/5 grid place-items-center text-lifted-mute text-xs">
              No image
            </div>
          )}
          {galleryItems.map((m, i) =>
            m ? (
              <div key={i} className="aspect-[3/4] overflow-hidden rounded-xl bg-lifted-surface border border-white/5">
                <SafeImage src={m} alt={`${p.title} view ${i + 1}`} className="w-full h-full object-contain" />
              </div>
            ) : null,
          )}
        </div>
        {/* Details */}
        <div className="lg:sticky lg:top-24 self-start">
          {p.collection_slug && (
            <Link to={`/collections/${p.collection_slug}`} className="text-xs text-lifted-mute font-bebas tracking-[0.18em] uppercase" data-testid="product-collection-link">
              ← {p.collection_slug.replace("-", " ")}
            </Link>
          )}
          <h1 className="font-anton uppercase text-3xl sm:text-4xl mt-2" data-testid="product-title">{p.title}</h1>
          <div className="font-anton text-2xl text-lifted-green mt-3" data-testid="product-price">
            {hasPrice ? `$${(displayCents / 100).toFixed(2)} USD` : "Price not available"}
          </div>
          {p.description && (
            <div className="text-lifted-dim text-sm mt-4 prose-invert" data-testid="product-description"
                 dangerouslySetInnerHTML={{ __html: p.description }} />
          )}

          {hasVariants && (
            <div className="mt-5" data-testid="product-variants">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bebas text-lifted-dim mb-2">
                {variants[0]?.option_name || "Size"}
              </div>
              <div className="flex flex-wrap gap-2">
                {variants.map(v => {
                  const active = v.id === selectedVariantId;
                  const disabled = !v.available;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => !disabled && setSelectedVariantId(v.id)}
                      disabled={disabled}
                      data-testid={`variant-${v.id}`}
                      className={`px-3 py-1.5 rounded-md border text-xs font-bebas uppercase tracking-[0.16em] transition-colors
                        ${active
                          ? "bg-lifted-green text-black border-lifted-green"
                          : disabled
                            ? "bg-black/30 text-lifted-dim border-white/5 line-through cursor-not-allowed"
                            : "bg-black/40 text-white border-white/15 hover:border-lifted-green/40"}`}
                    >
                      {v.title}
                      {v.inventory != null && v.inventory > 0 && v.inventory <= 3 && (
                        <span className="ml-1 text-[10px] text-amber-300">· {v.inventory} left</span>
                      )}
                    </button>
                  );
                })}
              </div>
              {variantBlocked && (
                <div className="text-[11px] text-amber-300 mt-2" data-testid="variant-required">
                  Please select a {variants[0]?.option_name?.toLowerCase() || "size"}.
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex gap-2">
            <button
              onClick={onAdd}
              disabled={!hasPrice || variantBlocked}
              className="btn-lifted flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="product-add-to-cart"
            >
              <ShoppingBag className="h-4 w-4" /> {added ? "Added to cart ✓" : "Add to cart"}
            </button>
            <button
              onClick={onBuyNow}
              disabled={!hasPrice || variantBlocked}
              className="btn-ghost disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="product-buy-now"
            >
              Buy now <ArrowRight className="h-3 w-3 ml-1" />
            </button>
          </div>
          {added && (
            <div className="mt-3 text-xs text-lifted-mute" data-testid="product-cart-link">
              Added to your bag — <Link to="/cart" className="text-lifted-green">go to checkout</Link>.
            </div>
          )}
          {!hasPrice && (
            <div className="mt-3 text-xs text-amber-300" data-testid="product-no-price">
              This product has no price set yet. Please check back soon or contact support.
            </div>
          )}

          <div className="mt-8 grid grid-cols-2 gap-2 text-xs">
            <div className="glass-panel p-3 flex items-center gap-2"><Truck className="h-4 w-4 text-lifted-green" /><div><div className="font-anton uppercase text-sm">Same-day</div><div className="text-lifted-mute">{p.same_day_eligible ? "Eligible in zones" : "Where available"}</div></div></div>
            <div className="glass-panel p-3 flex items-center gap-2"><Shield className="h-4 w-4 text-lifted-green" /><div><div className="font-anton uppercase text-sm">21+ verified</div><div className="text-lifted-mute">Age-gated</div></div></div>
          </div>
        </div>
      </div>

      {/* Related */}
      {p.related?.length > 0 && (
        <section className="mt-16" data-testid="product-related">
          <h3 className="eyebrow mb-4">You may also like</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {p.related.map((rp) => {
              const rpCents = rp.price_cents ?? Math.round((rp.price || 0) * 100);
              return (
                <Link key={rp.slug || rp.handle} to={`/products/${rp.slug || rp.handle}`} className="group block"
                      data-testid={`product-related-${rp.slug || rp.handle}`}>
                  <div className="aspect-[3/4] overflow-hidden rounded-xl bg-lifted-surface border border-white/5">
                    <SafeImage src={(rp.media || [])[0] || rp.image} alt={rp.title} loading="lazy" className="w-full h-full object-contain" />
                  </div>
                  <div className="mt-2 text-sm text-lifted-text group-hover:text-lifted-green">{rp.title}</div>
                  <div className="text-xs text-lifted-mute">
                    {rpCents ? `$${(rpCents / 100).toFixed(2)} USD` : "Price not available"}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
